import React, { useEffect, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { formatName } from "../../../helpers/helpers";
import { filterChatUsers } from "./chatSearchUtils";
import { ScheduleMessageMenu, ScheduleTimeModal } from "./ScheduleMessageMenu";
import { draftClientMessageId } from "./scheduledMessageUtils";

export const MAX_DIRECT_MESSAGE_RECIPIENTS = 7;

const permissionLabel = (permissionType) => {
  switch (permissionType) {
    case "company_admin":
      return "Admin";
    case "company_manager":
      return "Manager";
    case "rightwork_internal":
      return "Rightwork";
    default:
      return null;
  }
};

// Slack-style "New message" pane: pick people, write, and the conversation
// (1:1 or group DM) is created on the first send, so abandoned composes
// never leave empty conversations behind.
function NewMessageComposer({
  chatUsers = [],
  onSend,
  onSchedule,
  canCreateChannel = false,
  onCreateChannel,
}) {
  const [recipients, setRecipients] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [scheduleAnchor, setScheduleAnchor] = useState(null);
  const [customScheduleOpen, setCustomScheduleOpen] = useState(false);
  const draftId = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (recipients.length > 0) {
      textareaRef.current?.focus();
    }
  }, [recipients.length]);

  // Grow with content like Stream's autosize textarea (capped at ~10 rows)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight) || 20;
    textarea.style.height = `${Math.min(textarea.scrollHeight, lineHeight * 10)}px`;
  }, [text]);

  const atCap = recipients.length >= MAX_DIRECT_MESSAGE_RECIPIENTS;
  const canSend = recipients.length > 0 && text.trim().length > 0 && !sending;

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await onSend(recipients, text.trim());
    } catch (submitError) {
      setError(
        submitError?.response?.data?.system?.message ||
          "We couldn't start this conversation. Please try again."
      );
      setSending(false);
    }
  };

  // "Schedule message": same conversation creation as a send, but the text
  // posts later. Errors show inline, like a failed send.
  const schedule = async (sendAt) => {
    if (!canSend || !onSchedule) return;
    setSending(true);
    setError(null);
    try {
      const clientMessageId = draftClientMessageId(
        draftId,
        `${recipients.map((recipient) => recipient.id).join(",")}|${text.trim()}`
      );
      await onSchedule(recipients, text.trim(), sendAt, clientMessageId);
    } catch (scheduleError) {
      setError(
        scheduleError?.response?.data?.system?.message ||
          "We couldn't schedule this message. Please try again."
      );
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const recipientSummary = () => {
    if (recipients.length === 0) {
      return "Add one person for a direct message, or several for a group message.";
    }
    if (recipients.length === 1) {
      return `Direct message with ${formatName(recipients[0].name)}.`;
    }
    return `Group message with ${recipients.length} people. Group messages have no name or moderators; for something ongoing, create a channel.`;
  };

  return (
    <div className="str-chat rw-new-message flex h-full min-h-0 flex-col">
      <div className="border-b border-slate-100 px-6 py-4">
        <p className="text-[18px] leading-[18px]">New message</p>
        <p className="mt-1 text-sm text-slate-500">
          Start a direct or group message
        </p>
      </div>

      <div className="flex items-start gap-x-3 border-b border-slate-100 px-6 py-3">
        <span className="shrink-0 text-sm font-medium leading-[30px] text-slate-500">
          To:
        </span>
        <Autocomplete
          multiple
          fullWidth
          autoHighlight
          openOnFocus
          options={chatUsers}
          value={recipients}
          onChange={(event, value) => {
            setRecipients(value.slice(0, MAX_DIRECT_MESSAGE_RECIPIENTS));
            setError(null);
          }}
          inputValue={inputValue}
          onInputChange={(event, value) => setInputValue(value)}
          getOptionLabel={(option) => formatName(option.name)}
          isOptionEqualToValue={(option, value) =>
            option.id?.toString() === value.id?.toString()
          }
          filterOptions={(options, state) =>
            filterChatUsers(options, state.inputValue)
          }
          getOptionDisabled={(option) =>
            atCap &&
            !recipients.some(
              (recipient) => recipient.id?.toString() === option.id?.toString()
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                size="small"
                label={formatName(option.name)}
                sx={{ bgcolor: "#ede9fe", color: "#5b21b6" }}
              />
            ))
          }
          renderOption={(optionProps, option) => {
            const role = permissionLabel(option.permission_type);
            return (
              <li {...optionProps} key={option.id}>
                <div className="flex w-full min-w-0 items-center gap-x-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-700 text-sm text-white">
                    {formatName(option.name)?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-x-2">
                      <span className="truncate">{formatName(option.name)}</span>
                      {role && (
                        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xxs text-slate-600">
                          {role}
                        </span>
                      )}
                    </div>
                    {option.email && (
                      <div className="truncate text-xs text-slate-500">
                        {option.email}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              variant="standard"
              placeholder={
                recipients.length ? "" : "Type a name or email"
              }
              InputProps={{ ...params.InputProps, disableUnderline: true }}
              sx={{
                // Keep every row (placeholder, input, chips) 30px tall so
                // the To: label lines up with the first row
                "& .MuiInputBase-root": { paddingTop: 0, paddingBottom: 0 },
                "& .MuiInputBase-input": {
                  height: "22px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },
                "& .MuiChip-root": { marginTop: "3px", marginBottom: "3px" },
              }}
            />
          )}
        />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
        <p className="max-w-md text-sm text-slate-500">{recipientSummary()}</p>
        {atCap && (
          <p className="mt-2 max-w-md text-xs text-slate-400">
            Group messages hold up to {MAX_DIRECT_MESSAGE_RECIPIENTS + 1} people.
          </p>
        )}
        {canCreateChannel && (
          <button
            type="button"
            className="mt-3 text-sm font-medium text-violet-700 hover:text-violet-900"
            onClick={() => onCreateChannel?.(recipients)}
          >
            Create a channel instead
          </button>
        )}
      </div>

      {error && (
        <div className="mx-6 mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Same markup/classes as Stream's MessageInputFlat so the compose box
          is visually identical to the real one (attachments arrive once the
          conversation exists) */}
      <div className="py-1 border-t border-slate-100">
        <div className="str-chat__message-input">
          <div className="str-chat__message-input-inner">
            <div className="str-chat__message-textarea-container">
              <div className="str-chat__message-textarea-with-emoji-picker">
                <div className="rta str-chat__textarea str-chat__message-textarea-react-host">
                  <textarea
                    ref={textareaRef}
                    className="rta__textarea str-chat__textarea__textarea str-chat__message-textarea"
                    data-testid="message-input"
                    rows={1}
                    placeholder={
                      recipients.length ? "Type your message" : "Add people first"
                    }
                    aria-label="Type your message"
                    value={text}
                    disabled={recipients.length === 0 || sending}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              className="str-chat__send-button"
              data-testid="send-button"
              aria-label="Send"
              disabled={!canSend}
              onClick={submit}
            >
              {sending ? (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              ) : (
                <svg
                  fill="currentColor"
                  height="24"
                  viewBox="0 0 24 24"
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Send</title>
                  <path d="M4.00952 22L24 12L4.00952 2L4 9.77778L18.2857 12L4 14.2222L4.00952 22Z" />
                </svg>
              )}
            </button>
            {onSchedule && (
              <button
                type="button"
                aria-label="Schedule message"
                title="Schedule for later"
                disabled={!canSend}
                onClick={(event) => setScheduleAnchor(event.currentTarget)}
                className="ml-0.5 flex h-8 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ExpandLessIcon fontSize="small" />
              </button>
            )}
          </div>
        </div>
      </div>
      <ScheduleMessageMenu
        anchorEl={scheduleAnchor}
        onClose={() => setScheduleAnchor(null)}
        onPick={schedule}
        onCustom={() => setCustomScheduleOpen(true)}
      />
      <ScheduleTimeModal
        open={customScheduleOpen}
        onClose={() => setCustomScheduleOpen(false)}
        onConfirm={({ sendAt }) => schedule(sendAt)}
      />
    </div>
  );
}

export default NewMessageComposer;
